import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { connectionApi, type Connection, type ConnectionSetupDefaults } from '../../lib/connections-api';
import { ConnectionCredentials, EndpointCredential, type CredentialInput } from './ConnectionCredentials';
import { connectionStarters, type ConnectionStarter } from './ConnectionsIntroduction';
import { ConnectionAbilities, abilitySelections, endpointReady, selectedAbilities, selectionPayload, type AbilitySelection } from './ConnectionAbilities';
import { liveState, useReadinessClock } from './connection-state';
import { Icon } from '../ui/Icon';
import styles from './Connections.module.css';

interface SetupFormProps {
  connection?: Connection;
  starter?: ConnectionStarter;
  defaults?: ConnectionSetupDefaults;
  setupDraftId?: string;
  initialConnection?: Connection;
  onProgress?: (connection: Connection) => void;
  onSaved: (connection: Connection) => void;
  onClose: () => void;
  inline?: boolean;
  title?: string;
}

export function SetupDialog(props: SetupFormProps) {
  const dialog = useRef<HTMLDialogElement>(null);
  const backdropPress = useRef(false);
  const title = useId();
  useEffect(() => {
    const element = dialog.current!;
    const previous = document.activeElement as HTMLElement | null;
    element.showModal();
    return () => { if (element.open) element.close(); previous?.focus({ preventScroll: true }); };
  }, []);
  const dismiss = () => dialog.current?.querySelector<HTMLButtonElement>('[aria-label="Close connection setup"]')?.click();
  const outside = (element: HTMLDialogElement, x: number, y: number) => {
    const bounds = element.getBoundingClientRect();
    return x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom;
  };
  return createPortal(<dialog ref={dialog} className={styles.setupDialog} aria-labelledby={title}
    onPointerDown={event => { backdropPress.current = event.target === event.currentTarget && outside(event.currentTarget, event.clientX, event.clientY); }}
    onClick={event => {
      if (backdropPress.current && event.target === event.currentTarget && outside(event.currentTarget, event.clientX, event.clientY)) dismiss();
      backdropPress.current = false;
    }}
    onCancel={event => {
      event.preventDefault();
      dismiss();
    }}>
    <ConnectionSetupForm {...props} title={title} />
  </dialog>, document.body);
}

export function ConnectionSetupForm({ connection, starter = 'custom', defaults, setupDraftId, initialConnection,
  onProgress, onSaved, onClose, inline = false, title: suppliedTitle }: SetupFormProps) {
  const ownTitle = useId();
  const title = suppliedTitle ?? ownTitle;
  const [repairing, setRepairing] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  const [choosingProtocol, setChoosingProtocol] = useState(!defaults?.protocol && starter !== 'stripe' && starter !== 'agent');
  const [saved, setSaved] = useState<Connection | null>(initialConnection ?? null);
  const [completed, setCompleted] = useState(initialConnection?.enabled ?? false);
  const [selections, setSelections] = useState<AbilitySelection[]>(initialConnection ? abilitySelections(initialConnection) : []);
  useReadinessClock();
  useEffect(() => {
    if (initialConnection) {
      setSaved(initialConnection);
      if (!initialConnection.enabled) setCompleted(false);
    }
  }, [initialConnection]);
  useEffect(() => {
    if (!inline) form.current?.querySelector<HTMLElement>('[data-setup-focus]')?.focus({ preventScroll: true });
  }, [choosingProtocol, !!saved, completed, inline]);
  const preset = connectionStarters[starter];
  const [name, setName] = useState(connection?.name ?? defaults?.name ?? preset.name),
    [url, setUrl] = useState(connection ? '' : defaults?.url ?? (defaults?.protocol && defaults.protocol !== preset.protocol ? '' : preset.url)),
    [protocol, setProtocol] = useState<'' | 'mcp' | 'a2a'>(
      defaults?.protocol ?? (starter === 'stripe' || starter === 'agent' ? preset.protocol : ''),
    ),
    [instructions, setInstructions] = useState(
      connection ? '' : defaults?.instructions ?? preset.instructions,
    );
  const credentials = useRef<CredentialInput>(null);
  const cache = useQueryClient();
  const remember = (value: Connection) => {
    void cache.invalidateQueries({ queryKey: ['connection'] });
    void cache.invalidateQueries({ queryKey: ['connections'] });
    void cache.invalidateQueries({ queryKey: ['navigation-status'] });
    setSaved(value);
    onProgress?.(value);
  };
  const save = useMutation({
    mutationFn: () => {
      if (!protocol) throw new Error('Choose MCP or A2A to continue.');
      return connectionApi.setup({
        ...(setupDraftId ? { setupDraftId } : {}),
        name,
        url,
        protocol,
        auth: credentials.current?.value() ?? { mode: 'none' },
        instructions,
        ...(connection
          ? {
              connectionId: connection.id,
              expectedRevision: connection.revision,
            }
          : {}),
      });
    },
    onSuccess: (c) => {
      credentials.current?.clear();
      remember(c);
      setSelections(abilitySelections(c));
    },
    onSettled: () => credentials.current?.clear(),
  });
  const activate = useMutation({
    mutationFn: async () => {
      if (!saved) throw new Error('Discover the connection first.');
      const updated = await connectionApi.patch(saved.id, {
        expectedRevision: saved.revision,
        selections: selectionPayload(saved, selections),
        enabled: true,
      });
      remember(updated);
      setCompleted(true);
      // Enabling invalidates the old readiness lease. Verify the selected access
      // before claiming that Echo can use it; the worker remains the fallback.
      return connectionApi.check(updated.id);
    },
    onSuccess: remember,
  });
  const recheck = useMutation({
    onMutate: () => activate.reset(),
    mutationFn: () => connectionApi.check(saved!.id),
    onSuccess: remember,
  });
  const busy = save.isPending || activate.isPending || recheck.isPending;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (saved) return;
    if (!choosingProtocol && protocol && !save.isPending) save.mutate();
  };
  const chooseProtocol = (value: 'mcp' | 'a2a') => {
    if (url === preset.url && value !== preset.protocol) setUrl('');
    else if (!url && value === preset.protocol) setUrl(preset.url);
    setProtocol(value);
    setChoosingProtocol(false);
    save.reset();
  };
  const dismiss = () => {
    if (busy) return;
    if (saved) onSaved(saved);
    else onClose();
  };
  return (
      <form ref={form} className={styles.card} onSubmit={submit} aria-labelledby={title}>
        <header className={styles.modalHeading}>
          <h2 id={title}>
            {saved ? completed ? 'Connection saved' : 'Connection found' : connection
              ? 'Add or reconnect an endpoint'
              : `Connect ${starter === 'custom' ? 'a tool or service' : starter === 'agent' ? 'another agent' : preset.title}`}
          </h2>
          <button type="button" aria-label="Close connection setup" disabled={busy} onClick={dismiss}>
            <Icon name="x" size={18} />
          </button>
        </header>
        {saved ? <>
          <div className={styles.setupOutcome} role="status" aria-live="polite">
            <span className={styles.outcomeIcon} data-ready={completed ? liveState(saved) === 'ready' : saved.endpoints.some(endpointReady)}>
              <Icon name={busy ? 'history-toggle' : (completed ? liveState(saved) === 'ready' : saved.endpoints.some(endpointReady)) ? 'check' : 'link'} size={25} />
            </span>
            <h3 data-setup-focus tabIndex={-1}>{busy ? 'Verifying access…' : completed
              ? liveState(saved) === 'ready' ? `${saved.name} is on` : 'Access saved · Verification needed'
              : saved.endpoints.some(endpointReady) ? `Connected to ${saved.name}` : 'Connection saved · Needs attention'}</h3>
            <p>{completed ? liveState(saved) === 'ready'
              ? 'Echo can now use your selected abilities with the permissions you chose.'
              : 'Your permissions are saved. The endpoint must pass a fresh check before Echo can use it.'
              : saved.endpoints.some(endpointReady)
                ? `${saved.capabilities.filter((cap) => cap.available).length} abilities discovered. Choose what Echo can use, then turn on access.`
                : 'We saved the endpoint, but could not verify access. Check its address and update its credential below.'}</p>
          </div>
          {saved.endpoints.map(endpoint => <div key={endpoint.id}>
            <p className={styles.muted}>{endpoint.url}</p>
            <EndpointCredential connection={saved} endpoint={endpoint} onSaved={remember} />
          </div>)}
          {!saved.disconnected_at && <button type="button" disabled={busy} onClick={() => setRepairing(true)}>Add or reconnect an endpoint</button>}
          {repairing && <SetupDialog connection={saved} onSaved={value => { remember(value); setRepairing(false); }} onClose={() => setRepairing(false)} />}
          {completed && !saved.disconnected_at && <button type="button" disabled={busy} onClick={() => {
            setSelections(abilitySelections(saved)); setCompleted(false);
          }}>Edit abilities</button>}
          {!completed && <>
            <div className={styles.sectionHeading}><div><h3>What can Echo use?</h3><p>Selected abilities ask for review unless you explicitly allow trusted reads.</p></div></div>
            <ConnectionAbilities connection={saved} selections={selections} onChange={setSelections} disabled={busy} />
            {!selectedAbilities(saved, selections).length && <p className={styles.muted}>Select at least one ability to turn on this connection.</p>}
          </>}
          {saved.endpoints.filter((endpoint) => endpoint.safe_error).map((endpoint) =>
            <p role="alert" className={styles.error} key={endpoint.id}>{endpoint.safe_error}</p>)}
          {(activate.isError || recheck.isError) && <p role="alert" className={styles.error}>{(activate.error ?? recheck.error)?.message}</p>}
          <div className={styles.modalActions}>
            {!completed ? <>
              <button type="button" disabled={busy} onClick={dismiss}>{saved.enabled ? 'Keep current permissions' : 'Finish later'}</button>
              <button className={styles.primaryButton} type="button"
                disabled={busy || !!saved.disconnected_at || !saved.endpoints.some(endpointReady) || !selectedAbilities(saved, selections).length}
                onClick={() => activate.mutate()}>{busy ? 'Turning on…' : saved.enabled ? 'Save permissions' : 'Turn on connection'}</button>
            </> : <button className={styles.primaryButton} type="button" disabled={busy} onClick={dismiss}>{inline ? 'Done' : 'View connection'} <Icon name="chevron-right" size={16} /></button>}
            {!busy && (activate.isError || recheck.isError || !(completed ? liveState(saved) === 'ready' : saved.endpoints.some(endpointReady))) &&
              <button type="button" onClick={() => recheck.mutate()}>Check again</button>}
          </div>
          {!completed && !saved.enabled && <p className={styles.modalFootnote}>Finish later saves the connection with access off.</p>}
        </> : <>
        <fieldset className={styles.setupFields} disabled={save.isPending}>
          {choosingProtocol ? <>
            <h3 className={styles.protocolChoiceTitle}>Choose a connection type</h3>
            <div className={styles.protocolChoices}>
              <button type="button" className={styles.protocolChoice} data-setup-focus onClick={() => chooseProtocol('mcp')}>
                <span className={styles.protocolHeading}>
                  <span className={`${styles.protocolMark} ${styles.mcpMark}`} aria-hidden="true" />
                  <strong>MCP</strong>
                </span>
                <strong>Tools and knowledge</strong>
                <span>Let Echo search notes, retrieve data, and use tools from another service.</span>
                <span className={styles.protocolChoiceAction}>Connect with MCP <Icon name="chevron-right" size={16} /></span>
              </button>
              <button type="button" className={styles.protocolChoice} onClick={() => chooseProtocol('a2a')}>
                <span className={styles.protocolHeading}>
                  <span className={`${styles.protocolMark} ${styles.a2aMark}`} aria-hidden="true" />
                  <strong>A2A</strong>
                </span>
                <strong>Another agent</strong>
                <span>Let Echo delegate work to another AI agent, follow its progress, and bring back its findings.</span>
                <span className={styles.protocolChoiceAction}>Connect with A2A <Icon name="chevron-right" size={16} /></span>
              </button>
            </div>
            <p className={styles.muted}>
              Choose one protocol for this endpoint. If your system offers both,
              you can add the other endpoint to the same connection later.
            </p>
          </> : <>
            <div className={styles.row}>
              <button type="button" onClick={() => setChoosingProtocol(true)}>Change connection type</button>
              <strong>{protocol === 'mcp' ? 'MCP · Tools and knowledge' : 'A2A · Another agent'}</strong>
            </div>
            {!connection && (
              <div className={styles.setupNeeds}>
                <strong>Before you start</strong>
                <p>{starter !== 'brain' && protocol !== preset.protocol
                  ? connectionStarters[protocol === 'a2a' ? 'agent' : 'custom'].needs
                  : preset.needs}</p>
                <span>
                  Next, review its abilities and turn on access here.
                </span>
              </div>
            )}
            {!connection && (
              <label>
                Name
                <input
                  data-setup-focus
                  required
                  maxLength={200}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
            )}
            <div className={styles.connectionFields}>
              <p className={styles.muted}>
                Paste the connection URL supplied by the service or its administrator.
                It must be reachable over public HTTPS. A URL on your own computer
                needs a hosted connection before Resonance can reach it.
              </p>
              <label>
                {protocol === 'a2a' ? 'Agent Card URL' : 'Endpoint URL'}
                <input
                  data-setup-focus={connection ? true : undefined}
                  required
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={
                    protocol === 'a2a'
                      ? 'https://example.com/.well-known/agent-card.json'
                      : 'https://example.com/mcp'
                  }
                />
              </label>
              <ConnectionCredentials ref={credentials} initialMode={starter === 'stripe' ? 'bearer' : 'none'} />
              <details>
                <summary>Supported connections and authentication</summary>
                <p className={styles.muted}>
                  MCP Streamable HTTP and A2A 0.3.0 JSON-RPC are supported. Public
                  endpoints, encrypted bearer credentials and client certificates (mTLS) are supported. Browser
                  OAuth, API-key headers and local transports are not available yet.
                  For Stripe, use a restricted or agent API key authorized for its MCP
                  server.
                </p>
              </details>
            </div>
            {!connection && (
              <label>
                How should Resonance use this?
                <textarea
                  maxLength={8000}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                />
              </label>
            )}
          </>}
        </fieldset>
        <div className={styles.modalActions}>
          <button type="button" disabled={save.isPending} onClick={dismiss}>Cancel</button>
          {!choosingProtocol && <button className={styles.primaryButton} type="submit" disabled={save.isPending || !protocol}>
            {save.isPending ? 'Connecting…' : 'Connect and review abilities'}
          </button>}
        </div>
        {!choosingProtocol && <p className={styles.muted}>
          Discovery does not run tools or start agent tasks. Select permissions
          after discovery.
        </p>}
        {save.isError && (
          <p role="alert" className={styles.error}>
            {save.error.message}
          </p>
        )}
        </>}
      </form>
  );
}
